import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { eventDay } from '../lib/eventStats';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import { interactiveRow } from './a11y';
import useActivationDevices from '@/src/Activations/components/hooks/useActivationDevices';
import useDeviceTelemetry, {
    PrintRollup,
    QueueData,
} from '@/src/DeviceTelemetry/hooks/useDeviceTelemetry';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { useEventContext } from './EventContext';
import { toast } from 'sonner';
import useDeviceKeys from '@/src/DeviceManagement/components/hooks/useDeviceKeys';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import { FeatureFlags } from '@/src/Features/lib/constants';
import { useContext } from 'react';
import RemoteControlLink from '../../RemoteControl/components/RemoteControlLink';

/*
 * EventDevices — the live-ops strip on the event Overview: every device
 * uploading to THIS event, with uploaded/remaining/prints, friendly-name
 * inline edit, and Remote/Telemetry links. Replaces the classic details
 * tab's Queue Data card (the #1 unbridged gap in the parity map).
 * Devices refresh via the hook's 30s poll; queue + prints poll at 10s.
 */

const ACTIVE_WINDOW_MIN = 3;
const STUCK_WINDOW_MIN = 10;

interface Row {
    deviceKeyId: string;
    telemetryDeviceId?: string;
    displayName: string;
    friendlyName: string | null;
    uploaded?: number;
    remaining?: number;
    prints?: number;
    lastActivity: string | null;
    /* unified list (2026-08-20): assigned devices are first-class rows
       — quiet = assigned with no event activity yet (online state
       comes from the device key's lastUsed); an activity row WITHOUT
       assigned is the anomaly worth seeing */
    assigned?: boolean;
    quiet?: boolean;
    borrowed?: boolean;
}

export default function EventDevices({
    activationId,
}: {
    activationId: number;
}) {
    const navigate = useNavigate();
    const { orgId } = useUserOrg();
    const { devices, updateFriendlyName } =
        useActivationDevices(activationId);
    const { getQueueDataForActivation, getPrintRollupForActivation } =
        useDeviceTelemetry();
    const authenticatedApi = useAuthenticatedApi();
    /* gallery comes from the event context — one resolution per
       event; the old private fetch had no retry, so one transient
       failure left galleryId null and Save silently no-oped */
    const { galleryId } = useEventContext();
    const { getDeviceKeys } = useDeviceKeys(orgId);
    const { getActivations } = useActivations();

    const [queue, setQueue] = useState<QueueData[]>([]);
    const [rollup, setRollup] = useState<PrintRollup>({
        total: 0,
        devices: [],
    });
    const [editing, setEditing] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [allDevices, setAllDevices] = useState<any[]>([]);
    const [allGroups, setAllGroups] = useState<any[]>([]);
    const [assignedDevices, setAssignedDevices] = useState<number[]>([]);
    const [assignedGroups, setAssignedGroups] = useState<number[]>([]);
    const [assignEdit, setAssignEdit] = useState(false);
    const [draftDevices, setDraftDevices] = useState<Set<number>>(new Set());
    const [draftGroups, setDraftGroups] = useState<Set<number>>(new Set());
    const [assignBusy, setAssignBusy] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);
    /* names straight from the assignment rows — covers borrowed devices
       whose owner-org lists we don't have */
    const [assignedNames, setAssignedNames] = useState<
        Record<string, string>
    >({});
    /* cross-org borrowing (GlobalAdmin) */
    const featureFlags = useContext(FeatureFlagContext) as any;
    const isGlobalAdmin = !!featureFlags?.[FeatureFlags.GLOBAL_ADMIN_V1];
    const [orgs, setOrgs] = useState<any[] | null>(null);
    const [borrowOrgId, setBorrowOrgId] = useState('');
    const [orgCatalog, setOrgCatalog] = useState<
        Record<number, { devices: any[]; groups: any[] }>
    >({});
    /* selections persist per-org across org switches (classic loses them) */
    const [crossDevices, setCrossDevices] = useState<
        Record<number, Set<number>>
    >({});
    const [crossGroups, setCrossGroups] = useState<
        Record<number, Set<number>>
    >({});
    const [keptBorrowedDevices, setKeptBorrowedDevices] = useState<
        Set<number>
    >(new Set());
    const [keptBorrowedGroups, setKeptBorrowedGroups] = useState<
        Set<number>
    >(new Set());
    const [pickQuery, setPickQuery] = useState('');
    const [scopeAll, setScopeAll] = useState(false);
    /* org-wide assignments + activation dates → per-device conflicts on
       the event date ("free that night") */
    const [orgAssignRows, setOrgAssignRows] = useState<any[] | null>(null);
    const [actDates, setActDates] = useState<Record<number, string>>({});
    const [eventDate, setEventDate] = useState<string | null>(null);

    const loadBorrowOrg = async (oid: number) => {
        if (orgCatalog[oid]) return;
        const [keys, groups] = await Promise.all([
            authenticatedApi
                .get(`${oid}/device-keys`)
                .then((r) => r.data)
                .catch(() => []),
            authenticatedApi
                .get(`${oid}/device-groups`)
                .then((r) => r.data)
                .catch(() => []),
        ]);
        setOrgCatalog((c) => ({
            ...c,
            [oid]: { devices: keys ?? [], groups: groups ?? [] },
        }));
    };

    useEffect(() => {
        if (!orgId || !activationId) return;
        let mounted = true;
        (async () => {
            const gid = galleryId;
            if (gid == null) return;
            const [devRows, grpRows, keys, groups] = await Promise.all([
                authenticatedApi
                    .get(`/device-management/assignments/gallery/${gid}`)
                    .then((r) => r.data)
                    .catch(() => []),
                authenticatedApi
                    .get(
                        `/${orgId}/device-groups/assignments/gallery/${gid}`,
                    )
                    .then((r) => r.data)
                    .catch(() => []),
                getDeviceKeys().catch(() => []),
                authenticatedApi
                    .get(`${orgId}/device-groups`)
                    .then((r) => r.data)
                    .catch(() => []),
            ]);
            if (!mounted) return;
            const names: Record<string, string> = {};
            (devRows ?? []).forEach((r: any) => {
                /* rows alias dk.key_id AS device_key_id (hex); the
                   numeric device_key.id is deviceId */
                const did = +(r.deviceId ?? r.device_id);
                if (Number.isFinite(did) && (r.deviceName ?? r.name))
                    names[`d${did}`] = r.deviceName ?? r.name;
            });
            (grpRows ?? []).forEach((r: any) => {
                const gid2 = +(r.groupId ?? r.group_id);
                if (Number.isFinite(gid2) && r.name)
                    names[`g${gid2}`] = r.name;
            });
            setAssignedNames(names);
            setAssignedDevices(
                (devRows ?? [])
                    .map((r: any) => +(r.deviceId ?? r.device_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
            setAssignedGroups(
                (grpRows ?? [])
                    .map((r: any) => +(r.groupId ?? r.group_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
            setAllDevices(keys ?? []);
            setAllGroups(groups ?? []);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, activationId, galleryId]);

    const saveAssignments = async () => {
        if (assignBusy) return;
        if (galleryId == null) {
            setAssignError(
                'No gallery on this event yet — assignments need one.',
            );
            return;
        }
        setAssignBusy(true);
        setAssignError(null);
        /* own-org ids never ride in crossOrg entries (classic mixed them
           up); kept-borrowed ids ride under orgId 0 — the server dedupes
           into one Set and ignores the orgId attribution */
        const crossDeviceEntries = Object.entries(crossDevices)
            .filter(([, set]) => set.size > 0)
            .map(([oid, set]) => ({
                orgId: +oid,
                deviceIds: Array.from(set),
            }));
        const alreadyPicked = new Set(
            crossDeviceEntries.flatMap((e) => e.deviceIds),
        );
        const keptDev = Array.from(keptBorrowedDevices).filter(
            (d) => !alreadyPicked.has(d),
        );
        if (keptDev.length > 0)
            crossDeviceEntries.push({ orgId: 0, deviceIds: keptDev });

        const crossGroupEntries = Object.entries(crossGroups)
            .filter(([, set]) => set.size > 0)
            .map(([oid, set]) => ({
                orgId: +oid,
                deviceGroups: Array.from(set),
            }));
        const pickedGroups = new Set(
            crossGroupEntries.flatMap((e) => e.deviceGroups),
        );
        const keptGrp = Array.from(keptBorrowedGroups).filter(
            (g) => !pickedGroups.has(g),
        );
        if (keptGrp.length > 0)
            crossGroupEntries.push({ orgId: 0, deviceGroups: keptGrp });

        const results = await Promise.all([
            authenticatedApi
                .post(
                    `/device-management/assignments/gallery/${galleryId}/create`,
                    {
                        deviceIds: Array.from(draftDevices),
                        ...(isGlobalAdmin && crossDeviceEntries.length > 0
                            ? { crossOrgDeviceIds: crossDeviceEntries }
                            : {}),
                    },
                )
                .catch(() => null),
            authenticatedApi
                .post(`/${orgId}/device-groups/assignments/gallery`, {
                    galleryId,
                    deviceGroups: Array.from(draftGroups),
                    ...(isGlobalAdmin && crossGroupEntries.length > 0
                        ? { crossOrgDeviceGroups: crossGroupEntries }
                        : {}),
                })
                .catch(() => null),
        ]);
        setAssignBusy(false);
        if (results.every((r) => r != null)) {
            setAssignEdit(false);
            /* re-read from the server — borrowed names/ids come from the
               assignment rows */
            const [devRows, grpRows] = await Promise.all([
                authenticatedApi
                    .get(
                        `/device-management/assignments/gallery/${galleryId}`,
                    )
                    .then((r) => r.data)
                    .catch(() => []),
                authenticatedApi
                    .get(
                        `/${orgId}/device-groups/assignments/gallery/${galleryId}`,
                    )
                    .then((r) => r.data)
                    .catch(() => []),
            ]);
            const names: Record<string, string> = {};
            (devRows ?? []).forEach((r: any) => {
                /* rows alias dk.key_id AS device_key_id (hex); the
                   numeric device_key.id is deviceId */
                const did = +(r.deviceId ?? r.device_id);
                if (Number.isFinite(did) && (r.deviceName ?? r.name))
                    names[`d${did}`] = r.deviceName ?? r.name;
            });
            (grpRows ?? []).forEach((r: any) => {
                const gid2 = +(r.groupId ?? r.group_id);
                if (Number.isFinite(gid2) && r.name)
                    names[`g${gid2}`] = r.name;
            });
            setAssignedNames(names);
            setAssignedDevices(
                (devRows ?? [])
                    .map((r: any) => +(r.deviceId ?? r.device_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
            setAssignedGroups(
                (grpRows ?? [])
                    .map((r: any) => +(r.groupId ?? r.group_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
            toast.success('Devices saved');
        } else {
            setAssignError('Save failed — try again.');
        }
    };

    useEffect(() => {
        if (!orgId || !activationId) return;
        let mounted = true;
        const load = async () => {
            const [q, r] = await Promise.all([
                getQueueDataForActivation(orgId, activationId).catch(
                    () => [],
                ),
                getPrintRollupForActivation(orgId, activationId).catch(
                    () => ({ total: 0, devices: [] }),
                ),
            ]);
            if (!mounted) return;
            setQueue(q ?? []);
            setRollup(r ?? { total: 0, devices: [] });
        };
        load();
        const timer = setInterval(load, 10 * 1000);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, activationId]);

    const rows = useMemo<Row[]>(() => {
        const prints = new Map(
            rollup.devices.map((d) => [d.deviceKeyId, d.prints]),
        );
        const byKey = new Map<string, Row>();
        devices.forEach((d) => {
            const q = queue.find((x) => x.deviceKeyId === d.deviceKeyId);
            const qd = (q?.queueData ?? {}) as {
                uploaded?: number;
                unuploaded?: number;
            };
            byKey.set(d.deviceKeyId, {
                deviceKeyId: d.deviceKeyId,
                telemetryDeviceId: q?.deviceId,
                displayName: d.displayName,
                friendlyName: d.friendlyName,
                uploaded: qd.uploaded,
                remaining: qd.unuploaded,
                prints: prints.get(d.deviceKeyId),
                lastActivity:
                    [d.lastUploadAt, q?.updatedAt]
                        .filter(Boolean)
                        .sort()
                        .pop() ?? null,
            });
        });
        queue.forEach((q) => {
            if (byKey.has(q.deviceKeyId)) return;
            const qd = (q.queueData ?? {}) as {
                uploaded?: number;
                unuploaded?: number;
            };
            byKey.set(q.deviceKeyId, {
                deviceKeyId: q.deviceKeyId,
                telemetryDeviceId: q.deviceId,
                displayName: (q as any).name || 'Unknown device',
                friendlyName: null,
                uploaded: qd.uploaded,
                remaining: qd.unuploaded,
                prints: prints.get(q.deviceKeyId),
                lastActivity: q.updatedAt ?? null,
            });
        });
        rollup.devices.forEach((d) => {
            if (byKey.has(d.deviceKeyId)) return;
            byKey.set(d.deviceKeyId, {
                deviceKeyId: d.deviceKeyId,
                displayName: 'Unknown device',
                friendlyName: null,
                prints: d.prints,
                lastActivity: null,
            });
        });
        /* assigned devices join as first-class rows; activity rows
           that aren't assigned get flagged instead of dominating */
        const assignedKeys = new Set<string>();
        assignedDevices.forEach((did) => {
            const key = allDevices.find((d: any) => d.id === did);
            const own = key != null;
            const keyId = key?.keyId ?? `assigned-${did}`;
            assignedKeys.add(keyId);
            if (byKey.has(keyId)) return;
            const lastUsed =
                key?.lastUsed ?? key?.lastUsedAt ?? key?.lastUploadAt;
            byKey.set(keyId, {
                deviceKeyId: keyId,
                displayName:
                    assignedNames[`d${did}`] ??
                    key?.name ??
                    `device ${did}`,
                friendlyName: null,
                lastActivity: lastUsed ?? null,
                assigned: true,
                quiet: true,
                borrowed: !own,
            });
        });
        byKey.forEach((row) => {
            row.assigned = row.assigned || assignedKeys.has(row.deviceKeyId);
        });
        return Array.from(byKey.values()).sort(
            (a, b) =>
                moment(b.lastActivity ?? 0).valueOf() -
                moment(a.lastActivity ?? 0).valueOf(),
        );
    }, [devices, queue, rollup, assignedDevices, allDevices, assignedNames]);

    const saveEdit = async (row: Row) => {
        const value = editValue.trim();
        setEditing(null);
        if (!value || value === (row.friendlyName ?? row.displayName))
            return;
        await updateFriendlyName(row.deviceKeyId, value);
    };

    /* ---- picker derivations ---- */
    const deviceOnline = (d: any) => {
        const t = d?.lastUsed ?? d?.lastUsedAt ?? d?.lastUploadAt;
        return !!t && moment().diff(moment(t), 'minutes') < 3;
    };
    const deviceMeta = (d: any) => {
        const t = d?.lastUsed ?? d?.lastUsedAt ?? d?.lastUploadAt;
        return t ? moment(t).fromNow() : 'never used';
    };
    /* same-day booking on another activation */
    const conflictFor = (deviceKeyId: number): string | null => {
        if (!eventDate || !orgAssignRows) return null;
        const hit = orgAssignRows.find((r: any) => {
            const did = +(r.deviceKeyId ?? r.device_key_id);
            const aid = +(r.activationId ?? r.activation_id);
            if (did !== deviceKeyId || aid === activationId) return false;
            if (r.isArchived ?? r.is_archived) return false;
            const date = actDates[aid];
            return (
                !!date &&
                eventDay(date).isSame(eventDay(eventDate), 'day')
            );
        });
        if (!hit) return null;
        const name =
            hit.activationName ?? hit.activation_name ?? 'another event';
        return `on ${name}`;
    };

    const toggleSet =
        (setter: React.Dispatch<React.SetStateAction<Set<number>>>) =>
        (id: number) =>
            setter((prev) => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
            });
    const toggleDraftDevice = toggleSet(setDraftDevices);
    const toggleDraftGroup = toggleSet(setDraftGroups);
    const toggleCross = (
        map: Record<number, Set<number>>,
        setMap: React.Dispatch<
            React.SetStateAction<Record<number, Set<number>>>
        >,
        oid: number,
        id: number,
    ) =>
        setMap((prev) => {
            const set = new Set(prev[oid] ?? []);
            set.has(id) ? set.delete(id) : set.add(id);
            return { ...prev, [oid]: set };
        });

    const draftChips = useMemo(() => {
        const chips: Array<{
            key: string;
            label: string;
            tag: string | null;
            conflict: boolean;
            remove: () => void;
        }> = [];
        draftGroups.forEach((gid) => {
            const g = allGroups.find((x: any) => +x.id === gid);
            chips.push({
                key: `g${gid}`,
                label: g?.name ?? assignedNames[`g${gid}`] ?? `group ${gid}`,
                tag: 'group',
                conflict: false,
                remove: () => toggleDraftGroup(gid),
            });
        });
        draftDevices.forEach((did) => {
            const d = allDevices.find((x: any) => +x.id === did);
            chips.push({
                key: `d${did}`,
                label:
                    d?.name ?? assignedNames[`d${did}`] ?? `device ${did}`,
                tag: null,
                conflict: conflictFor(did) != null,
                remove: () => toggleDraftDevice(did),
            });
        });
        keptBorrowedGroups.forEach((gid) => {
            chips.push({
                key: `kg${gid}`,
                label: assignedNames[`g${gid}`] ?? `group ${gid}`,
                tag: 'group · borrowed',
                conflict: false,
                remove: () =>
                    setKeptBorrowedGroups((prev) => {
                        const next = new Set(prev);
                        next.delete(gid);
                        return next;
                    }),
            });
        });
        keptBorrowedDevices.forEach((did) => {
            chips.push({
                key: `kd${did}`,
                label: assignedNames[`d${did}`] ?? `device ${did}`,
                tag: 'borrowed',
                conflict: false,
                remove: () =>
                    setKeptBorrowedDevices((prev) => {
                        const next = new Set(prev);
                        next.delete(did);
                        return next;
                    }),
            });
        });
        Object.entries(crossGroups).forEach(([oid, set]) =>
            set.forEach((gid) => {
                const g = orgCatalog[+oid]?.groups.find(
                    (x: any) => +x.id === gid,
                );
                chips.push({
                    key: `xg${oid}-${gid}`,
                    label: g?.name ?? `group ${gid}`,
                    tag: 'group · borrowed',
                    conflict: false,
                    remove: () =>
                        toggleCross(crossGroups, setCrossGroups, +oid, gid),
                });
            }),
        );
        Object.entries(crossDevices).forEach(([oid, set]) =>
            set.forEach((did) => {
                const d = orgCatalog[+oid]?.devices.find(
                    (x: any) => +x.id === did,
                );
                chips.push({
                    key: `xd${oid}-${did}`,
                    label: d?.name ?? `device ${did}`,
                    tag: 'borrowed',
                    conflict: false,
                    remove: () =>
                        toggleCross(
                            crossDevices,
                            setCrossDevices,
                            +oid,
                            did,
                        ),
                });
            }),
        );
        return chips;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        draftGroups,
        draftDevices,
        keptBorrowedGroups,
        keptBorrowedDevices,
        crossGroups,
        crossDevices,
        allGroups,
        allDevices,
        assignedNames,
        orgCatalog,
        orgAssignRows,
        eventDate,
        actDates,
    ]);

    const pickResults = useMemo(() => {
        type PickRow = {
            key: string;
            name: string;
            meta: string;
            online: boolean;
            free: boolean;
            conflict: string | null;
            added: boolean;
            toggle: () => void;
        };
        const q = pickQuery.trim().toLowerCase();
        const deviceRow = (d: any): PickRow => {
            const did = +d.id;
            const conflict = conflictFor(did);
            return {
                key: `d${did}`,
                name: d.name ?? `device ${did}`,
                meta: deviceMeta(d),
                online: deviceOnline(d),
                free: !!eventDate && !conflict,
                conflict,
                added: draftDevices.has(did),
                toggle: () => toggleDraftDevice(did),
            };
        };
        const groupRow = (g: any): PickRow => ({
            key: `g${g.id}`,
            name: g.name ?? `group ${g.id}`,
            meta: 'group — adds every member',
            online: true,
            free: false,
            conflict: null,
            added: draftGroups.has(+g.id),
            toggle: () => toggleDraftGroup(+g.id),
        });
        const sections: Array<{
            label: string;
            hint?: string;
            rows: PickRow[];
        }> = [];
        if (!q) {
            const ranked = [...allDevices]
                .filter((d: any) => !draftDevices.has(+d.id))
                .map(deviceRow)
                .sort(
                    (a, b) =>
                        Number(b.free) - Number(a.free) ||
                        Number(b.online) - Number(a.online) ||
                        a.name.localeCompare(b.name),
                )
                .slice(0, 6);
            sections.push({
                label: eventDate
                    ? `Suggested · free ${eventDay(eventDate).format('MMM D')}`
                    : 'Suggested',
                hint: 'date · presence',
                rows: ranked,
            });
            sections.push({
                label: 'Groups',
                rows: allGroups.slice(0, 4).map(groupRow),
            });
        } else {
            const gs = allGroups.filter((g: any) =>
                (g.name ?? '').toLowerCase().includes(q),
            );
            const ds = allDevices.filter((d: any) =>
                (d.name ?? '').toLowerCase().includes(q),
            );
            if (gs.length)
                sections.push({
                    label: `Groups · ${gs.length}`,
                    rows: gs.map(groupRow),
                });
            sections.push({
                label: `Devices · ${ds.length}`,
                rows: ds.map(deviceRow),
            });
        }
        if (scopeAll && borrowOrgId && orgCatalog[+borrowOrgId]) {
            const oid = +borrowOrgId;
            const cat = orgCatalog[oid];
            const orgName =
                (orgs ?? []).find((o: any) => o.id === oid)?.name ?? 'org';
            const bg = cat.groups.filter(
                (g: any) => !q || (g.name ?? '').toLowerCase().includes(q),
            );
            const bd = cat.devices.filter(
                (d: any) => !q || (d.name ?? '').toLowerCase().includes(q),
            );
            sections.push({
                label: `${orgName} · borrow`,
                hint: 'GlobalAdmin',
                rows: [
                    ...bg.map((g: any) => ({
                        key: `xg${g.id}`,
                        name: g.name ?? `group ${g.id}`,
                        meta: 'group — adds every member',
                        online: true,
                        free: false,
                        conflict: null,
                        added: crossGroups[oid]?.has(+g.id) ?? false,
                        toggle: () =>
                            toggleCross(
                                crossGroups,
                                setCrossGroups,
                                oid,
                                +g.id,
                            ),
                    })),
                    ...bd.map((d: any) => ({
                        key: `xd${d.id}`,
                        name: d.name ?? `device ${d.id}`,
                        meta: deviceMeta(d),
                        online: deviceOnline(d),
                        free: false,
                        conflict: null,
                        added: crossDevices[oid]?.has(+d.id) ?? false,
                        toggle: () =>
                            toggleCross(
                                crossDevices,
                                setCrossDevices,
                                oid,
                                +d.id,
                            ),
                    })),
                ].slice(0, 30),
            });
        }
        return sections;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pickQuery,
        allDevices,
        allGroups,
        draftDevices,
        draftGroups,
        scopeAll,
        borrowOrgId,
        orgCatalog,
        crossDevices,
        crossGroups,
        orgs,
        eventDate,
        actDates,
        orgAssignRows,
    ]);

    if (
        rows.length === 0 &&
        assignedDevices.length === 0 &&
        assignedGroups.length === 0 &&
        !assignEdit &&
        galleryId == null
    )
        return null;

    return (
        <section className="mb-10">
            <div className="mb-1.5 flex items-baseline justify-between">
                <span className="ps-label">Devices on this event</span>
                <span className="flex items-baseline gap-3.5">
                    {rollup.total > 0 && (
                        <span className="text-[12px] text-muted-foreground">
                            <b className="font-semibold text-foreground tabular-nums">
                                {rollup.total.toLocaleString()}
                            </b>{' '}
                            print{rollup.total === 1 ? '' : 's'} this event
                        </span>
                    )}
                    {!assignEdit ? (
                        <button
                            type="button"
                            onClick={() => {
                                const ownDeviceIds = new Set(
                                    allDevices.map((d2: any) => d2.id),
                                );
                                const ownGroupIds = new Set(
                                    allGroups.map((g2: any) => g2.id),
                                );
                                setDraftDevices(
                                    new Set(
                                        assignedDevices.filter((d2) =>
                                            ownDeviceIds.has(d2),
                                        ),
                                    ),
                                );
                                setKeptBorrowedDevices(
                                    new Set(
                                        assignedDevices.filter(
                                            (d2) => !ownDeviceIds.has(d2),
                                        ),
                                    ),
                                );
                                setDraftGroups(
                                    new Set(
                                        assignedGroups.filter((g2) =>
                                            ownGroupIds.has(g2),
                                        ),
                                    ),
                                );
                                setKeptBorrowedGroups(
                                    new Set(
                                        assignedGroups.filter(
                                            (g2) => !ownGroupIds.has(g2),
                                        ),
                                    ),
                                );
                                setCrossDevices({});
                                setCrossGroups({});
                                setBorrowOrgId('');
                                setPickQuery('');
                                setScopeAll(false);
                                setAssignEdit(true);
                                if (orgAssignRows == null) {
                                    authenticatedApi
                                        .get(
                                            '/device-management/assignments/org',
                                        )
                                        .then((r) =>
                                            setOrgAssignRows(r.data ?? []),
                                        )
                                        .catch(() => setOrgAssignRows([]));
                                    getActivations()
                                        .then((rows: any[]) => {
                                            const dates: Record<
                                                number,
                                                string
                                            > = {};
                                            (rows ?? []).forEach((a: any) => {
                                                if (a?.id && a?.date)
                                                    dates[+a.id] = a.date;
                                            });
                                            setActDates(dates);
                                            const self = (rows ?? []).find(
                                                (a: any) =>
                                                    +a.id === activationId,
                                            );
                                            if (self?.date)
                                                setEventDate(self.date);
                                        })
                                        .catch(() => {});
                                }
                                if (isGlobalAdmin && orgs == null)
                                    authenticatedApi
                                        .get('/orgs')
                                        .then((r) =>
                                            setOrgs(r.data ?? []),
                                        )
                                        .catch(() => setOrgs([]));
                            }}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                        >
                            Assign
                        </button>
                    ) : (
                        <span className="flex items-center gap-2.5">
                            {assignError && (
                                <span className="text-[11px] font-semibold text-attention">
                                    {assignError}
                                </span>
                            )}
                            <button
                                type="button"
                                disabled={assignBusy}
                                onClick={saveAssignments}
                                className="cursor-pointer rounded-full border-0 bg-brand px-3.5 py-1 font-[inherit] text-[11.5px] font-semibold text-brand-foreground"
                            >
                                {assignBusy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setAssignEdit(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </span>
                    )}
                </span>
            </div>
            {assignEdit && (
                <div className="mb-3 rounded-[12px] border border-border p-3.5">
                    {/* draft chips — the state, not the catalog */}
                    <div className="flex min-h-[26px] flex-wrap items-center gap-1.5">
                        {draftChips.length === 0 && (
                            <span className="text-[12px] text-faint">
                                Nothing assigned — search below.
                            </span>
                        )}
                        {draftChips.map((c) => (
                            <span
                                key={c.key}
                                className={
                                    'inline-flex items-center gap-1.5 rounded-full border py-[3px] pl-2.5 pr-1 text-[12px] font-semibold ' +
                                    (c.conflict
                                        ? 'border-attention/50'
                                        : 'border-border')
                                }
                            >
                                {c.label}
                                {c.tag && (
                                    <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                        {c.tag}
                                    </span>
                                )}
                                {c.conflict && (
                                    <span className="font-mono text-[9px] uppercase tracking-[0.06em] text-attention">
                                        booked
                                    </span>
                                )}
                                <button
                                    type="button"
                                    aria-label={`Remove ${c.label}`}
                                    onClick={c.remove}
                                    className="cursor-pointer rounded-full border-0 bg-transparent px-1 text-[12px] leading-none text-faint hover:bg-secondary hover:text-attention"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>

                    {/* search — icon, input, and scope share one baseline */}
                    <div className="mb-1 mt-2.5 flex h-9 items-center gap-2.5 border-b border-line px-0.5">
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.7"
                            strokeLinecap="round"
                            className="block flex-none text-faint"
                        >
                            <circle cx="11" cy="11" r="6.5" />
                            <path d="M15.8 15.8 20 20" />
                        </svg>
                        <input
                            value={pickQuery}
                            onChange={(e) => setPickQuery(e.target.value)}
                            placeholder="Add a device or group…"
                            aria-label="Search devices and groups"
                            className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 font-[inherit] text-[13px] text-foreground outline-none placeholder:text-faint"
                        />
                        {isGlobalAdmin && (
                            <button
                                type="button"
                                title="Search every organization's fleet"
                                aria-pressed={scopeAll}
                                onClick={() => {
                                    setScopeAll((v) => !v);
                                    if (orgs == null)
                                        authenticatedApi
                                            .get('/orgs')
                                            .then((r) =>
                                                setOrgs(r.data ?? []),
                                            )
                                            .catch(() => setOrgs([]));
                                }}
                                className={
                                    'flex-none cursor-pointer rounded-full border px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] ' +
                                    (scopeAll
                                        ? 'border-transparent bg-brand-soft text-brand-ink'
                                        : 'border-border bg-transparent text-muted-foreground')
                                }
                            >
                                All orgs
                            </button>
                        )}
                    </div>

                    {scopeAll && (
                        <div className="flex items-center gap-2 border-b border-line px-0.5 py-1.5">
                            <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                Org
                            </span>
                            <select
                                className="ps-input w-[220px] cursor-pointer appearance-none text-[12px]"
                                value={borrowOrgId}
                                onChange={(e) => {
                                    setBorrowOrgId(e.target.value);
                                    if (e.target.value)
                                        loadBorrowOrg(+e.target.value);
                                }}
                            >
                                <option value="">Choose…</option>
                                {(orgs ?? [])
                                    .filter((o: any) => o.id !== orgId)
                                    .map((o: any) => (
                                        <option key={o.id} value={o.id}>
                                            {o.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    )}

                    <div className="max-h-[320px] overflow-y-auto overflow-x-hidden pt-1">
                        {pickResults.map((section) => (
                            <div key={section.label}>
                                <div className="flex items-baseline justify-between px-1 pb-0.5 pt-2 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                    <span>{section.label}</span>
                                    {section.hint && (
                                        <span className="tracking-[0.06em] opacity-70">
                                            {section.hint}
                                        </span>
                                    )}
                                </div>
                                {section.rows.map((r) => (
                                    <div
                                        key={r.key}
                                        {...interactiveRow(r.toggle)}
                                        className="flex cursor-pointer items-center gap-2.5 rounded-[10px] px-1.5 py-2 hover:bg-sunk"
                                    >
                                        <span
                                            className={
                                                'h-[7px] w-[7px] flex-none rounded-full ' +
                                                (r.online
                                                    ? 'bg-good'
                                                    : 'bg-off')
                                            }
                                        />
                                        <span className="flex-none text-[13px] font-semibold">
                                            {r.name}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-[12px] text-faint">
                                            {r.meta}
                                        </span>
                                        {r.conflict ? (
                                            <span className="flex-none whitespace-nowrap font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-attention">
                                                {r.conflict}
                                            </span>
                                        ) : r.free ? (
                                            <span className="flex-none font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-good">
                                                free
                                            </span>
                                        ) : null}
                                        <span
                                            className={
                                                'flex-none rounded-full border px-2.5 py-[3px] text-[11px] font-semibold ' +
                                                (r.added
                                                    ? 'border-transparent bg-brand-soft text-brand-ink'
                                                    : 'border-border text-muted-foreground')
                                            }
                                        >
                                            {r.added ? 'Added' : '+ Add'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ))}
                        {pickResults.every((s) => s.rows.length === 0) && (
                            <p className="px-1 py-4 text-[12.5px] text-faint">
                                {scopeAll
                                    ? 'No matches in the loaded orgs — pick an org above to load its fleet.'
                                    : isGlobalAdmin
                                      ? 'No matches — try All orgs to borrow from another organization.'
                                      : 'No matches.'}
                            </p>
                        )}
                    </div>
                </div>
            )}
            {rows.map((row, i) => {
                const minsSince = row.lastActivity
                    ? moment().diff(moment(row.lastActivity), 'minutes')
                    : null;
                const active =
                    minsSince != null && minsSince < ACTIVE_WINDOW_MIN;
                const stuck =
                    (row.remaining ?? 0) > 0 &&
                    (minsSince == null || minsSince > STUCK_WINDOW_MIN);
                return (
                    <div
                        key={row.deviceKeyId}
                        className={
                            'group flex flex-wrap items-center gap-3 py-2.5 ' +
                            (i > 0 ? 'border-t border-line' : '')
                        }
                    >
                        {/* status column — kit "Live elements" pattern */}
                        <span className="flex w-[72px] flex-none items-center gap-2">
                            <span
                                className={
                                    'h-2 w-2 flex-none rounded-full ' +
                                    (active ? 'ps-pulse' : 'bg-off')
                                }
                            />
                            {active && (
                                <span className="font-mono text-[10px] font-semibold tracking-[0.1em] text-good">
                                    ONLINE
                                </span>
                            )}
                        </span>
                        <div className="min-w-0 flex-1 basis-[150px] sm:flex-none sm:basis-auto sm:w-[210px]">
                            {editing === row.deviceKeyId ? (
                                <input
                                    autoFocus
                                    className="ps-input w-full text-[13px] font-semibold"
                                    value={editValue}
                                    onChange={(e) =>
                                        setEditValue(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveEdit(row);
                                        if (e.key === 'Escape')
                                            setEditing(null);
                                    }}
                                    onBlur={() => saveEdit(row)}
                                />
                            ) : (
                                <span
                                    className={
                                        'flex items-center gap-1.5 text-[13px] font-semibold ' +
                                        (active ? '' : 'text-muted-foreground')
                                    }
                                >
                                    <span className="truncate">
                                        {row.displayName}
                                    </span>
                                    {!row.assigned && (
                                        <span
                                            title="This device uploaded here without being assigned"
                                            className="flex-none font-mono text-[9px] font-semibold tracking-[0.1em] text-attention"
                                        >
                                            NOT ASSIGNED
                                        </span>
                                    )}
                                    {row.borrowed && (
                                        <span className="flex-none font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                            borrowed
                                        </span>
                                    )}
                                    {!row.quiet && (
                                        <button
                                            type="button"
                                            title="Rename for this event"
                                            onClick={() => {
                                                setEditing(
                                                    row.deviceKeyId,
                                                );
                                                setEditValue(
                                                    row.friendlyName ??
                                                        row.displayName,
                                                );
                                            }}
                                            className="cursor-pointer border-0 bg-transparent p-0 text-[11px] text-faint opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
                                        >
                                            ✎
                                        </button>
                                    )}
                                </span>
                            )}
                        </div>
                        <div className="flex min-w-0 flex-1 flex-wrap gap-x-5 gap-y-1">
                            {row.quiet && (
                                <span className="whitespace-nowrap text-[12px] text-faint">
                                    no uploads yet
                                </span>
                            )}
                            {[
                                {
                                    label: 'Uploaded',
                                    value: row.uploaded,
                                    warn: false,
                                },
                                {
                                    label: 'Remaining',
                                    value: row.remaining,
                                    warn: stuck,
                                },
                                {
                                    label: 'Prints',
                                    value: row.prints,
                                    warn: false,
                                },
                            ]
                                .filter((c) => c.value != null)
                                .map((c) => (
                                    <span
                                        key={c.label}
                                        className="whitespace-nowrap text-[12.5px]"
                                    >
                                        <b
                                            className={
                                                'font-semibold tabular-nums ' +
                                                (c.warn
                                                    ? 'text-attention'
                                                    : '')
                                            }
                                        >
                                            {Number(
                                                c.value,
                                            ).toLocaleString()}
                                        </b>{' '}
                                        <span
                                            className={
                                                'font-mono text-[9px] uppercase tracking-[0.1em] ' +
                                                (c.warn
                                                    ? 'font-semibold text-attention'
                                                    : 'text-faint')
                                            }
                                        >
                                            {c.label}
                                        </span>
                                    </span>
                                ))}
                        </div>
                        <span className="ml-auto flex flex-none items-center gap-3.5">
                            <span className="whitespace-nowrap text-[11.5px] text-faint">
                                {row.lastActivity
                                    ? moment(row.lastActivity).fromNow()
                                    : '—'}
                            </span>
                            <RemoteControlLink
                                orgId={orgId}
                                deviceKeyId={row.deviceKeyId}
                                interfaceVersion="v2"
                                className="text-[12px] font-semibold text-brand-ink"
                            />
                            {row.telemetryDeviceId && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `/orgs/${orgId}/device-telemetry/${row.telemetryDeviceId}`,
                                        )
                                    }
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                                >
                                    Telemetry
                                </button>
                            )}
                        </span>
                    </div>
                );
            })}
            {!assignEdit && assignedGroups.length > 0 && (
                    <div
                        className={
                            'flex flex-wrap gap-1.5 ' +
                            (rows.length > 0
                                ? 'border-t border-line pt-2.5'
                                : '')
                        }
                    >
                        {assignedGroups.map((gid2) => {
                            const own = allGroups.some(
                                (g2: any) => g2.id === gid2,
                            );
                            return (
                                <span
                                    key={`g${gid2}`}
                                    className="rounded-full bg-secondary px-2.5 py-[3px] text-[11.5px] font-semibold text-muted-foreground"
                                >
                                    {assignedNames[`g${gid2}`] ??
                                        allGroups.find(
                                            (g2: any) => g2.id === gid2,
                                        )?.name ??
                                        `group ${gid2}`}
                                    <span className="ml-1 font-mono text-[9px] uppercase text-faint">
                                        {own ? 'group' : 'group · borrowed'}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                )}
            {!assignEdit &&
                rows.length === 0 &&
                assignedDevices.length === 0 &&
                assignedGroups.length === 0 && (
                    <p className="py-1 text-[13px] text-muted-foreground">
                        No devices assigned — nothing can upload to this
                        event yet.
                    </p>
                )}
        </section>
    );
}
